import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/cooler-fault-reports';

// NOT: Bu controller (CoolerFaultReportsController) OData değil; REST/MediatR
// stili (query-param + ApiResponse zarfı, take limiti) çalışır. Bu yüzden
// useC360List (OData $top/$skip/$filter bekler) yerine veriyi tek seferde
// (take=500) çekip filtre/sıralama/sayfalamayı c360 görünümünde istemci
// tarafında yapıyoruz. Görsel olarak diğer c360 listeleriyle birebir aynıdır.

const STATUS_META = {
  Open: { label: 'Açık', tone: 'wait' },
  InProgress: { label: 'Devam', tone: 'wait' },
  Resolved: { label: 'Çözüldü', tone: 'ok' },
  Cancelled: { label: 'İptal', tone: 'neutral' },
};

const STATUS_OPTIONS = [
  { value: 'Open', label: 'Açık' },
  { value: 'InProgress', label: 'Devam' },
  { value: 'Resolved', label: 'Çözüldü' },
  { value: 'Cancelled', label: 'İptal' },
];

const URGENCY_META = {
  CRITICAL: { label: 'Kritik', tone: 'bad' },
  HIGH: { label: 'Yüksek', tone: 'wait' },
  NORMAL: { label: 'Normal', tone: 'neutral' },
  LOW: { label: 'Düşük', tone: 'neutral' },
};

const URGENCY_OPTIONS = [
  { value: 'CRITICAL', label: 'Kritik' },
  { value: 'HIGH', label: 'Yüksek' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Düşük' },
];

const REASON_LABEL = {
  COMPRESSOR: 'Kompresör',
  DOOR: 'Kapı',
  LEAK: 'Sızıntı',
  ELECTRICAL: 'Elektrik',
  NOT_COOLING: 'Soğutmuyor',
  OTHER: 'Diğer',
};

const REASON_OPTIONS = Object.entries(REASON_LABEL).map(([value, label]) => ({ value, label }));

// C360Table filtre popover'ları için kolon meta'sı (görsel + tip).
const COLUMN_FILTER_META = {
  coolerBarcode: { type: 'text', odataField: 'CoolerBarcode' },
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  reason: { type: 'select', odataField: 'Reason', options: REASON_OPTIONS },
  urgency: { type: 'select', odataField: 'Urgency', options: URGENCY_OPTIONS },
  trackingNo: { type: 'text', odataField: 'TrackingNo' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};

const SEARCH_FIELDS = ['coolerBarcode', 'customerTitle', 'note', 'trackingNo'];

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    inProgressCount: pick(data, 'inProgressCount', 'InProgressCount') ?? 0,
    resolvedCount: pick(data, 'resolvedCount', 'ResolvedCount') ?? 0,
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount') ?? 0,
    criticalOpenCount: pick(data, 'criticalOpenCount', 'CriticalOpenCount') ?? 0,
  };
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status];
  return <StatusBadge tone={meta?.tone || 'neutral'}>{meta?.label || status || '-'}</StatusBadge>;
}

function urgencyBadgeFor(urgency) {
  const meta = URGENCY_META[urgency];
  return <StatusBadge tone={meta?.tone || 'neutral'}>{meta?.label || urgency || '-'}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

// İstemci-tarafı kolon filtresi (C360Table filter shape: { op, value, value2 }).
function matchColumnFilter(meta, filter, rawValue) {
  if (!filter || !meta) return true;
  const op = filter.op;
  const isEmpty = rawValue === null || rawValue === undefined || rawValue === '';
  if (op === 'blank') return isEmpty;
  if (op === 'notBlank') return !isEmpty;

  if (meta.type === 'text') {
    const needle = String(filter.value ?? '').toLocaleLowerCase('tr-TR');
    if (!needle) return true;
    const hay = String(rawValue ?? '').toLocaleLowerCase('tr-TR');
    switch (op) {
      case 'notContains': return !hay.includes(needle);
      case 'equals': return hay === needle;
      case 'notEquals': return hay !== needle;
      case 'startsWith': return hay.startsWith(needle);
      case 'endsWith': return hay.endsWith(needle);
      case 'contains':
      default: return hay.includes(needle);
    }
  }

  if (meta.type === 'select') {
    if (filter.value === '' || filter.value == null) return true;
    return String(rawValue ?? '') === String(filter.value);
  }

  return true;
}

export function CoolerFaultsPage() {
  usePageMeta('Soğutucu Arızaları', 'Saha temsilcisinin mobile\'dan açtığı arıza bildirimleri');
  const navigate = useNavigate();
  const { canEdit } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ openCount: 0, inProgressCount: 0, resolvedCount: 0, cancelledCount: 0, criticalOpenCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [selected, setSelected] = useState(null);

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        openCount: data.openCount,
        inProgressCount: data.inProgressCount,
        resolvedCount: data.resolvedCount,
        cancelledCount: data.cancelledCount,
        criticalOpenCount: data.criticalOpenCount,
      });
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);

  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);

  const applyFilterText = useCallback((text) => {
    setFilterText(text);
    setCurrentPage(1);
  }, []);

  // Serbest metin + kolon filtreleri (istemci tarafı).
  const filteredRows = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    return allRows.filter((row) => {
      if (needle) {
        const hit = SEARCH_FIELDS.some((f) => {
          const v = pick(row, f, f.charAt(0).toUpperCase() + f.slice(1));
          return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
        });
        if (!hit) return false;
      }
      for (const [colId, filter] of Object.entries(columnFilters)) {
        const meta = COLUMN_FILTER_META[colId];
        const raw = pick(row, colId, colId.charAt(0).toUpperCase() + colId.slice(1));
        if (!matchColumnFilter(meta, filter, raw)) return false;
      }
      return true;
    });
  }, [allRows, filterText, columnFilters]);

  const totalCount = filteredRows.length;

  // Kayıt görüntüleme/yönetme artık tam-sayfa detaya (/cooler-faults/:id) gider.
  // GET-by-id olmadığı için satır verisini location.state.row ile taşıyoruz.
  const openDetail = useCallback((row) => {
    const id = pick(row, 'id', 'Id');
    navigate(`/cooler-faults/${id}`, { state: { row } });
  }, [navigate]);
  // #282: kalem (Yönet) düzenleme moduna açar
  const openEditRow = useCallback((row) => {
    const id = pick(row, 'id', 'Id');
    navigate(`/cooler-faults/${id}?edit=1`, { state: { row } });
  }, [navigate]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('reportedAt', {
      id: 'reportedAt', header: 'Bildirim', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('coolerBarcode', {
      id: 'coolerBarcode', header: 'Cihaz', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const barcode = pick(row.original, 'coolerBarcode', 'CoolerBarcode');
        const door = pick(row.original, 'affectedDoor', 'AffectedDoor');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span className="c360-erp-chip">{barcode || '-'}</span>
            {door != null && <span style={{ fontSize: 11, color: '#6b7280' }}>Kapı #{door}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('customerTitle', {
      id: 'customerTitle', header: 'Nokta', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('reason', {
      id: 'reason', header: 'Sebep', meta: { filterType: 'select' },
      cell: (info) => { const r = info.getValue(); return REASON_LABEL[r] || r || '-'; },
    }),
    columnHelper.accessor('urgency', {
      id: 'urgency', header: 'Öncelik', meta: { filterType: 'select' },
      cell: (info) => urgencyBadgeFor(info.getValue()),
    }),
    columnHelper.accessor('trackingNo', {
      id: 'trackingNo', header: 'Takip No', meta: { filterType: 'text' },
      cell: (info) => {
        const tn = info.getValue();
        return tn
          ? <span className="c360-erp-chip">{tn}</span>
          : <span style={{ color: '#cbd5e1' }}>(atanmamış)</span>;
      },
    }),
    columnHelper.accessor('status', {
      id: 'status', header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => statusBadgeFor(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Yönet" aria-label="Yönet" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, openDetail, openEditRow]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: false,
    manualPagination: false,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Bildirim', valueGetter: ({ row }) => formatDate(pick(row, 'reportedAt', 'ReportedAt')) },
        { headerName: 'Cihaz', valueGetter: ({ row }) => pick(row, 'coolerBarcode', 'CoolerBarcode') || '' },
        { headerName: 'Nokta', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Sebep', valueGetter: ({ row }) => { const r = pick(row, 'reason', 'Reason'); return REASON_LABEL[r] || r || ''; } },
        { headerName: 'Öncelik', valueGetter: ({ row }) => URGENCY_META[pick(row, 'urgency', 'Urgency')]?.label || pick(row, 'urgency', 'Urgency') || '' },
        { headerName: 'Takip No', valueGetter: ({ row }) => pick(row, 'trackingNo', 'TrackingNo') || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const data = filteredRows;
      if (data.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      exportToExcel(data, cols, `cooler-arizalari_${dateStr}`);
      showToast('success', `${data.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Barkod / nokta / not / TN..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginLeft: 8 }}>
        {counts.criticalOpenCount > 0 && badge('Kritik+Açık', counts.criticalOpenCount, 'red')}
        {badge('Açık', counts.openCount, 'orange')}
        {badge('Devam', counts.inProgressCount, 'blue')}
        {badge('Çözüldü', counts.resolvedCount, 'green')}
        {badge('İptal', counts.cancelledCount, 'gray')}
      </span>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={table.getRowModel().rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Soğutucu Arızaları"
        subtitle="Saha temsilcisinin mobile'dan açtığı soğutucu arıza bildirimleri. Servis takip no atayın, durum güncelleyin. Yeni kayıt sadece mobile push ile gelir."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => openDetail(row)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default CoolerFaultsPage;
